import { Autocomplete, Box, IconButton, Tooltip } from "@mui/material";
import { WarehouseAxios } from "api/WarehouseAxios";

import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { MecordColors } from "Constants/Color";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import { autocompleteDropdownStyles, filterPopoverStyles, tableStyles } from "Constants/Style";
import { useFormik } from "formik";
import DOMPurify from "dompurify";
import React, { useEffect, useState } from "react";
import {
  Accordion,
  Col,
  Form,
  Row,
  Table,
  Modal,
  Button,
} from "react-bootstrap";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import TextEditor from "components/TextEditor/TextEditor";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createDispatch,
  fetchPackingProductsByPackingIDs,
  fetchSODropdownForDispatch,
} from "slices/Warehouse/Dispatch/thunk";
import * as Yup from "yup";
import { clearAllTableStates } from "utils/clearTableStates";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import AddTags from "../components/AddTagsForDispatch";

interface GRNFormValues {
  dispatch_date: string;
  initiated_by: string;
  sales_order: any;
  transporter_type: string;
  packing_ids: any[];
  shipping_address?: any;
  note?: string;
  tags?: string[];
  attached_files?: FileList | null;
  // Transport type related fields
  transporter_name?: string;
  person_number?: string;
  docket_number?: string;
  company_name?: string;
  vehicle_number?: string;
  driver_name?: string;
  driver_number?: string;
  carrier_name?: string;
  train_number?: string;
  booking_number?: string;
  origin?: string;
  flight_number?: string;
  awb_number?: string;
  scheduled_date?: string;
  expected_delivery_date?: string;
  transport_remarks?: string;
}

const CreateDispatch: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const warehouse_id =
    state?.warehouse_id ||
    (() => {
      try {
        return sessionStorage.getItem("dispatch-selected-warehouse-id") || "";
      } catch {
        return "";
      }
    })();
  const dispatch = useDispatch<any>();
  const [selesOrderData, setSelesOrderData] = useState<any>({});
  const [packingOptions, setPackingOptions] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPackingList, setIsLoadingPackingList] = useState(false);
  const [packingProducts, setPackingProducts] = useState<any[]>([]);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [modalProducts, setModalProducts] = useState<any[]>([]);
  const [modalPackingNumber, setModalPackingNumber] = useState<string>("");
  const [modalPackingIndex, setModalPackingIndex] = useState<number | null>(null);
  const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] =
    useState(false);
  const [additionalDetailsIndex, setAdditionalDetailsIndex] =
    useState<number | null>(null);
  const [additionalDetailsDraft, setAdditionalDetailsDraft] =
    useState<string>("");

  const transportTypeOptions = [
    "Self Pickup",
    "Courier",
    "Road",
    "Rail",
    "Air",
  ];

  const commonInputStyle = {
    fontSize: "0.8rem",
    minHeight: 38,
    padding: "0.375rem 0.75rem",
  };

  const commonLabelStyle = {
    fontSize: "0.8rem",
    marginBottom: 6,
    marginTop: 12,
  };

  const requiredLabel = (label: string) => (
    <>
      {label} <span className="text-danger">*</span>
    </>
  );

  const buildTransportDetails = (formValues: GRNFormValues) => {
    const details: Record<string, string> = {};
    const transportType = (formValues.transporter_type || "").toLowerCase();

    switch (transportType) {
      case "self pickup":
        if (formValues.transporter_name)
          details.person_name = formValues.transporter_name;
        if (formValues.person_number)
          details.person_number = formValues.person_number;
        break;
      case "courier":
        if (formValues.docket_number)
          details.docket_number = formValues.docket_number;
        if (formValues.company_name)
          details.company_name = formValues.company_name;
        break;
      case "road":
        if (formValues.vehicle_number)
          details.truck_number = formValues.vehicle_number;
        if (formValues.driver_name)
          details.driver_name = formValues.driver_name;
        if (formValues.driver_number)
          details.driver_mobile = formValues.driver_number;
        if (formValues.transporter_name)
          details.transporter_name = formValues.transporter_name;
        break;
      case "rail":
        if (formValues.carrier_name)
          details.carrier_name = formValues.carrier_name;
        if (formValues.train_number)
          details.train_number = formValues.train_number;
        if (formValues.booking_number)
          details.booking_number = formValues.booking_number;
        if (formValues.origin) details.origin = formValues.origin;
        if (formValues.origin) details.origin = formValues.origin;
        // if(formValues.transport_remarks)
        //   details.remark = formValues.transport_remarks;
        break;
      case "air":
        if (formValues.carrier_name)
          details.carrier_name = formValues.carrier_name;
        if (formValues.flight_number)
          details.flight_number = formValues.flight_number;
        if (formValues.awb_number) details.awb_number = formValues.awb_number;
        if (formValues.origin) details.origin = formValues.origin;
        break;
      default:
        break;
    }

    // Add remarks for all transport types
    if (formValues.transport_remarks) {
      details.remarks = formValues.transport_remarks;
    }

    return details;
  };

  // Validation Schema
  const validationSchema = Yup.object().shape({
    dispatch_date: Yup.string().required("Dispatch Date is required"),
    initiated_by: Yup.string().trim().required("Initiated By is required"),
    sales_order: Yup.object().nullable().required("SO Number is required"),
    shipping_address: Yup.object()
      .nullable()
      .required("Shipping Address is required"),
    transporter_type: Yup.string().required("Transport Type is required"),
    packing_ids: Yup.array(),
    // .min(1, "At least one Packing ID is required")
    // .required("Packing ID is required"),
    // All fields are now optional
    transporter_name: Yup.string().trim(),
    person_number: Yup.string().trim(),
    docket_number: Yup.string().trim(),
    company_name: Yup.string().trim(),
    vehicle_number: Yup.string().trim(),
    driver_name: Yup.string().trim(),
    driver_number: Yup.string().trim(),
    carrier_name: Yup.string().trim(),
    train_number: Yup.string().trim(),
    booking_number: Yup.string().trim(),
    origin: Yup.string().trim(),
    flight_number: Yup.string().trim(),
    awb_number: Yup.string().trim(),
    // attached_files: Yup.mixed().test(
    //   "file-count",
    //   "Maximum 5 files allowed",
    //   (value) => {
    //     if (!value) return true; // Optional field
    //     if (value instanceof FileList) {
    //       return value.length <= 5;
    //     }
    //     return true;
    //   }
    // ),
  });

  const formik = useFormik<GRNFormValues>({
    initialValues: {
      dispatch_date: new Date()?.toISOString()?.split("T")?.[0],
      initiated_by: "",
      sales_order: null,
      shipping_address: null,
      transporter_type: "",
      packing_ids: [],
      note: "",
      tags: [],
      attached_files: null,
      // Transport type related fields
      transporter_name: "",
      person_number: "",
      docket_number: "",
      company_name: "",
      vehicle_number: "",
      driver_name: "",
      driver_number: "",
      carrier_name: "",
      train_number: "",
      booking_number: "",
      origin: "",
      flight_number: "",
      awb_number: "",
      scheduled_date: "",
      expected_delivery_date: "",
      transport_remarks: "",
    },
    validationSchema: validationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values, { setTouched, validateForm, setFieldError }) => {
      // Mark all fields as touched to show validation errors
      const fields = Object.keys(values);
      const touchedFields: any = {};
      fields.forEach((field) => {
        touchedFields[field] = true;
      });
      setTouched(touchedFields);

      // Validate form
      const errors = await validateForm();

      // If there are validation errors, show toast and return
      if (Object.keys(errors).length > 0) {
        toast.error("Please fill all mandatory fields.");
        return;
      }

      const selectedPackingIds = values.packing_ids || [];
      const hasPackingIds =
        Array.isArray(selectedPackingIds) && selectedPackingIds.length > 0;
      const hasPackingListLoaded =
        packingProducts && packingProducts.length > 0;

      // Block submit: require at least one packing ID
      if (!hasPackingIds) {
        setFieldError("packing_ids", "Please select at least one packing ID.");
        // toast.error("Please select at least one packing ID.");
        setTimeout(
          () =>
            document
              .getElementById("packing_ids")
              ?.scrollIntoView({ behavior: "smooth", block: "center" }),
          100,
        );
        return;
      }

      // Block submit: require packing list to be loaded after selecting packing IDs
      if (!hasPackingListLoaded) {
        setFieldError(
          "packing_ids",
          "Please load the packing list before submitting.",
        );
        toast.error("Please load the packing list before submitting.");
        setTimeout(
          () =>
            document
              .getElementById("load-packing-list")
              ?.scrollIntoView({ behavior: "smooth", block: "center" }),
          100,
        );
        return;
      }

      setIsSubmitting(true);
      try {
        const transportDetails = buildTransportDetails(values);

        // Create FormData object
        const formData = new FormData();

        // Append simple fields
        if (values?.dispatch_date)
          formData.append("dispatch_date", values.dispatch_date);
        if (values?.initiated_by)
          formData.append("initiated_by", values.initiated_by);
        if (values?.sales_order?.sales_order_product_id)
          formData.append(
            "sales_order_product_id",
            values.sales_order.sales_order_product_id,
          );
        if (values?.shipping_address?.shipping_address_id)
          formData.append(
            "shipping_address_id",
            values.shipping_address.shipping_address_id,
          );
        if (selesOrderData?.customer_details?.customer_id)
          formData.append(
            "company_id",
            selesOrderData.customer_details.customer_id,
          );
        if (warehouse_id) formData.append("warehouse_id", warehouse_id);
        if (values?.transporter_type)
          formData.append("transporter_type", values.transporter_type);
        if (values?.note) formData.append("note", values.note);
        if (values?.tags && values.tags.length > 0)
          formData.append("tags", JSON.stringify(values.tags));

        // Append nested objects as JSON strings
        if (values?.shipping_address) {
          formData.append(
            "shipping_address_details",
            JSON.stringify(values.shipping_address),
          );
        }
        if (selesOrderData?.customer_details) {
          formData.append(
            "customer_details",
            JSON.stringify(selesOrderData.customer_details),
          );
        }
        if (selesOrderData?.bill_from) {
          formData.append(
            "bill_from",
            JSON.stringify(selesOrderData.bill_from),
          );
        }
        if (selesOrderData?.bill_to) {
          formData.append("bill_to", JSON.stringify(selesOrderData.bill_to));
        }
        if (transportDetails) {
          formData.append(
            "transporter_details",
            JSON.stringify(transportDetails),
          );
        }

        // Append arrays as JSON strings
        if (packingProducts && packingProducts.length > 0) {
          formData.append("products", JSON.stringify(packingProducts));
        }

        // Append total summary fields
        formData.append("subtotal", summaryTotals.subtotal.toString());
        formData.append(
          "total_product_discount",
          summaryTotals.total_product_discount.toString(),
        );
        formData.append(
          "net_taxable_amount",
          summaryTotals.net_taxable_amount.toString(),
        );
        formData.append(
          "total_gst_amount",
          summaryTotals.total_gst_amount.toString(),
        );
        formData.append("total", summaryTotals.total.toString());

        // Append files if any
        if (values?.attached_files && values.attached_files.length > 0) {
          Array.from(values.attached_files).forEach(
            (file: File, index: number) => {
              formData.append("attached_files", file);
            },
          );
        }

        console.log("FormData payload created");

        const response = await dispatch(createDispatch(formData)).unwrap();
        clearAllTableStates();
        toast.success("Dispatch created successfully");
        navigate("/warehouse-dispatch");
      } catch (error: any) {
        console.error("Error creating Dispatch:", error);
        toast.error(error?.message || "Failed to create Dispatch");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(formik.errors, formik.submitCount);

  useEffect(() => {
    if (warehouse_id) {
      const fetchDropdown = async () => {
        try {
          const res = await dispatch(
            fetchSODropdownForDispatch(warehouse_id),
          ).unwrap();
          setPackingOptions(res || []);
        } catch {
          toast.error("Failed to fetch SO Numbers for Dispatch");
        }
      };
      fetchDropdown();
    }
  }, [dispatch, warehouse_id]);

  const handleCancelButton = () => {
    navigate("/warehouse-dispatch");
  };

  const handleLoadPackingList = async () => {
    const packingIds = (formik.values.packing_ids || [])
      .map((item: any) => item.packing_done_id || item.packing_id)
      .filter(Boolean);

    if (packingIds.length === 0) {
      toast.error("Please select at least one packing ID");
      return;
    }
    // return;
    setIsLoadingPackingList(true);
    try {
      const response = await dispatch(
        fetchPackingProductsByPackingIDs(packingIds),
      ).unwrap();
      console.log("response in dispatch form: ", response);
      const productsData = Array.isArray(response)
        ? response
        : response?.products || [];
      const updated_product = productsData?.map((prod: any) => {
        const quantity = Number(prod?.products?.length ?? 0);

        const rate_in_currency =
          Number(prod?.unit_price) /
          Number(selesOrderData?.currency_rate_in_inr);

        const item_amount = quantity * rate_in_currency;

        // ---- Discount calculation ----
        let discount_amount = 0;
        console.log("this is the discount type => ", prod?.discount_type);
        if (
          prod?.discount_type === "%" ||
          prod?.discount_type === "percentage" ||
          prod?.discount_type === "percent"
        ) {
          console.log("this is the discount value => ", prod?.discount_value);
          discount_amount =
            (item_amount * Number(prod?.discount_value ?? 0)) / 100;
        } else {
          discount_amount = Number(prod?.discount_value ?? 0);
        }

        // Prevent over-discounting
        discount_amount = Math.min(discount_amount, item_amount);

        // ---- Net taxable amount ----
        const net_taxable_amount = item_amount - discount_amount;

        // ---- GST calculation ----
        const gst_percent = Number(prod?.gst_percent ?? 0);
        const gst_amount = (net_taxable_amount * gst_percent) / 100;

        // ---- Total amount ----
        const total_amount = net_taxable_amount + gst_amount;

        return {
          ...prod,
          quantity,
          rate_in_currency: rate_in_currency.toFixed(2),
          item_amount: item_amount.toFixed(2),
          discount_amount: discount_amount.toFixed(2),
          net_taxable_amount: net_taxable_amount.toFixed(2),
          gst_amount: gst_amount.toFixed(2),
          total: total_amount.toFixed(2),
        };
      });

      // setPackingProducts(response?.data || []);
      setPackingProducts(updated_product || []);

      toast.success(
        `Packing list loaded successfully for ${packingIds.length} items`,
      );
    } catch (error) {
      console.error("Error loading packing list:", error);
      toast.error("Failed to load packing list");
    } finally {
      setIsLoadingPackingList(false);
    }
  };

  const summaryTotals = React.useMemo(() => {
    return packingProducts.reduce(
      (acc, prod) => {
        acc.subtotal += Number(prod.item_amount || 0);
        acc.total_product_discount += Number(prod.discount_amount || 0);
        acc.net_taxable_amount += Number(prod.net_taxable_amount || 0);
        acc.total_gst_amount += Number(prod.gst_amount || 0);
        acc.total += Number(prod.total || 0);
        console.log("this is the total => ", Number(prod.discount_amount || 0));
        console.log("acc => ", acc);
        return acc;
      },
      {
        subtotal: 0,
        total_product_discount: 0,
        net_taxable_amount: 0,
        total_gst_amount: 0,
        total: 0,
      },
    );
  }, [packingProducts]);

  // Group terms and conditions by type
  const groupedTerms = React.useMemo(() => {
    const terms = selesOrderData?.terms_and_conditions || [];
    const grouped: Record<string, string[]> = {};

    terms.forEach((term: any) => {
      const type = term.type || "Other";
      if (!grouped[type]) {
        grouped[type] = [];
      }

      // Handle options which could be a string or an array of strings
      if (Array.isArray(term.options)) {
        grouped[type].push(...term.options);
      } else if (typeof term.options === "string") {
        // Some backends might send joined strings or newline separated
        if (term.options.includes("\n")) {
          grouped[type].push(...term.options.split("\n").filter(Boolean));
        } else {
          grouped[type].push(term.options);
        }
      } else if (term.option) {
        // Fallback for some formats
        grouped[type].push(term.option);
      }
    });

    // Remove duplicates from each type's options
    Object.keys(grouped).forEach((type) => {
      grouped[type] = Array.from(new Set(grouped[type]));
    });

    return Object.entries(grouped);
  }, [selesOrderData?.terms_and_conditions]);

  // Helper function to format numbers to 2 decimal places

  // Get currency symbol from sales order or default to ₹
  const currencySymbol = selesOrderData?.currency_symbol || "₹";

  return (
    <PageContainer>
      <PageBreadcrumb
        title="Create Dispatch"
        titleLink="/warehouse-dispatch"
        breadcrumbs={[
          { label: "Dispatch", to: "/warehouse-dispatch" },
          { label: "Create Dispatch", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />

      <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
        {/* Dispatch Details Section */}
        <Row className="mb-3">
          <Col xs={12} md={8}>
            <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
              Dispatch Details
            </h6>
            <div
              className="p-3 rounded border mb-3"
              style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
            >
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <Form.Label
                    className="fw-semibold mt-0"
                    style={commonLabelStyle}
                  >
                    {requiredLabel("Dispatch Date")}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="dispatch_date"
                    value={formik.values.dispatch_date}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if ((e.target.value || "").trim())
                        setTimeout(
                          () =>
                            formik.setFieldError("dispatch_date", undefined),
                          0,
                        );
                    }}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.dispatch_date &&
                      formik.touched.dispatch_date
                    }
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.dispatch_date}
                  </Form.Control.Feedback>
                </Col>

                <Col xs={12} md={6}>
                  <Form.Label
                    className="fw-semibold mt-0"
                    style={commonLabelStyle}
                  >
                    {requiredLabel("Initiated By")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="initiated_by"
                    placeholder="Enter Name"
                    value={formik.values.initiated_by}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if ((e.target.value || "").trim())
                        setTimeout(
                          () => formik.setFieldError("initiated_by", undefined),
                          0,
                        );
                    }}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.initiated_by &&
                      formik.touched.initiated_by
                    }
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.initiated_by}
                  </Form.Control.Feedback>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    {requiredLabel("Packing ID")}
                  </Form.Label>
                  <Autocomplete
                    options={packingOptions}
                    getOptionLabel={(option: any) =>
                      typeof option === "string"
                        ? option
                        : option?.packing_number || ""
                    }
                    value={(formik.values.packing_ids || [])[0] || null}
                    onChange={(event, selected) => {
                      const onePacking = selected ? [selected] : [];
                      formik.setFieldValue("packing_ids", onePacking);
                      formik.setFieldValue(
                        "sales_order",
                        selected
                          ? {
                              sales_order_product_id:
                                (selected as any)?.sales_order_product_id,
                              sales_order_number:
                                (selected as any)?.sales_order_number,
                            }
                          : null,
                      );
                      formik.setFieldValue(
                        "shipping_address",
                        selected
                          ? {
                              ...((selected as any)?.shipping_address || {}),
                              shipping_address_id:
                                (selected as any)?.shipping_address_id,
                            }
                          : null,
                      );
                      if (selected) {
                        setSelesOrderData({
                          sales_order_product_id:
                            (selected as any)?.sales_order_product_id,
                          sales_order_number:
                            (selected as any)?.sales_order_number,
                          customer_details:
                            (selected as any)?.customer_details || {},
                          bill_from: (selected as any)?.bill_from || {},
                          bill_to: (selected as any)?.bill_to || {},
                          currency_rate_in_inr:
                            Number((selected as any)?.currency_rate_in_inr) || 1,
                          currency_symbol:
                            (selected as any)?.currency_symbol || "₹",
                        });
                        setTimeout(
                          () => formik.setFieldError("packing_ids", undefined),
                          0,
                        );
                      }
                    }}
                    onBlur={() => formik.setFieldTouched("packing_ids", true)}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt: any) => opt?.packing_number || "",
                      )
                    }
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select Packing ID"
                        name="packing_ids"
                        inputProps={{ ...params.inputProps, id: "packing_ids" }}
                        error={
                          formik.touched.packing_ids &&
                          Boolean(formik.errors.packing_ids)
                        }
                        helperText={
                          formik.touched.packing_ids &&
                          typeof formik.errors.packing_ids === "string"
                            ? formik.errors.packing_ids
                            : ""
                        }
                        fullWidth
                        size="small"
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&:hover fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#ced4da",
                        },
                      },
                    }}
                    // disabled={!packingOptions.length}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    {requiredLabel("SO Number")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formik.values?.sales_order?.sales_order_number || ""}
                    placeholder="Auto-filled after selecting packing"
                    style={commonInputStyle}
                    readOnly
                  />
                </Col>

                <Col xs={12} md={6}>
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    {requiredLabel("Shipping Address")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      formik.values?.shipping_address
                        ? `${formik.values.shipping_address?.address || ""}, ${formik.values.shipping_address?.city || ""}, ${formik.values.shipping_address?.state || ""}, ${formik.values.shipping_address?.pincode || formik.values.shipping_address?.pin_code || ""}`
                        : ""
                    }
                    placeholder="Auto-filled after selecting packing"
                    style={commonInputStyle}
                    readOnly
                  />
                </Col>
      
              </Row>
              <div id="load-packing-list">
                <PrimarySubmitButton
                  label="Load Packing List"
                  isLoading={isLoadingPackingList}
                  loadingLabel="Loading..."
                  onClick={handleLoadPackingList}
                  disabled={
                    !(formik.values.packing_ids || []).length ||
                    isLoadingPackingList
                  }
                  style={{
                    minWidth: "140px",
                    height: "38px",
                    fontSize: "0.8rem",
                  }}
                />
              </div>
            </div>
          </Col>

          <Col xs={12} md={4}>
            <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
              Transport Details
            </h6>
            <div
              className="p-3 rounded border mb-3"
              style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
            >
              <Row className="mb-3">
                <Col xs={12} md={12}>
                  <Form.Label
                    className="fw-semibold mt-0"
                    style={commonLabelStyle}
                  >
                    {requiredLabel("Transport Type")}
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={transportTypeOptions}
                    getOptionLabel={(option: any) =>
                      typeof option === "string" ? option : option || ""
                    }
                    value={formik.values.transporter_type || null}
                    onChange={(event, selected) => {
                      const transportType = selected || "";
                      formik.setFieldValue("transporter_type", transportType);
                      if (transportType)
                        setTimeout(
                          () =>
                            formik.setFieldError("transporter_type", undefined),
                          0,
                        );
                      // Clear all transport-related fields when transport type changes
                      formik.setFieldValue("transporter_name", "");
                      formik.setFieldValue("person_number", "");
                      formik.setFieldValue("docket_number", "");
                      formik.setFieldValue("company_name", "");
                      formik.setFieldValue("vehicle_number", "");
                      formik.setFieldValue("driver_name", "");
                      formik.setFieldValue("driver_number", "");
                      formik.setFieldValue("carrier_name", "");
                      formik.setFieldValue("train_number", "");
                      formik.setFieldValue("booking_number", "");
                      formik.setFieldValue("origin", "");
                      formik.setFieldValue("flight_number", "");
                      formik.setFieldValue("awb_number", "");
                      formik.setFieldValue("transport_remarks", "");
                    }}
                    onBlur={() =>
                      formik.setFieldTouched("transporter_type", true)
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt,
                      )
                    }
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        name="transporter_type"
                        placeholder="Select Transport Type"
                        error={
                          formik.touched.transporter_type &&
                          Boolean(formik.errors.transporter_type)
                        }
                        helperText={
                          formik.touched.transporter_type &&
                          formik.errors.transporter_type
                        }
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                </Col>
              </Row>
              {/* Self Pickup Fields */}
              {formik.values.transporter_type === "Self Pickup" && (
                <Row className="mb-3">
                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Transporter Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="transporter_name"
                      placeholder="Enter Transporter Name"
                      value={formik.values.transporter_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.transporter_name &&
                        formik.touched.transporter_name
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.transporter_name}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Person Number
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="person_number"
                      placeholder="Enter Person Number"
                      value={formik.values.person_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.person_number &&
                        formik.touched.person_number
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.person_number}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              )}

              {/* Courier Fields */}
              {formik.values.transporter_type === "Courier" && (
                <Row className="mb-3">
                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Docket Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="docket_number"
                      placeholder="Enter Docket Number"
                      value={formik.values.docket_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.docket_number &&
                        formik.touched.docket_number
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.docket_number}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Company Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="company_name"
                      placeholder="Enter Company Name"
                      value={formik.values.company_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.company_name &&
                        formik.touched.company_name
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.company_name}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              )}

              {/* Road Fields */}
              {formik.values.transporter_type === "Road" && (
                <Row className="mb-3">
                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Transporter Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="transporter_name"
                      placeholder="Enter Transporter Name"
                      value={formik.values.transporter_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.transporter_name &&
                        formik.touched.transporter_name
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.transporter_name}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Vehicle Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="vehicle_number"
                      placeholder="Enter Vehicle Number"
                      value={formik.values.vehicle_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.vehicle_number &&
                        formik.touched.vehicle_number
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.vehicle_number}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Driver Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="driver_name"
                      placeholder="Enter Driver Name"
                      value={formik.values.driver_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.driver_name &&
                        formik.touched.driver_name
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.driver_name}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Driver Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="driver_number"
                      placeholder="Enter Driver Number"
                      value={formik.values.driver_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.driver_number &&
                        formik.touched.driver_number
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.driver_number}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              )}

              {/* Rail Fields */}
              {formik.values.transporter_type === "Rail" && (
                <Row className="mb-3">
                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Carrier Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="carrier_name"
                      placeholder="Enter Carrier Name"
                      value={formik.values.carrier_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.carrier_name &&
                        formik.touched.carrier_name
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.carrier_name}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Train Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="train_number"
                      placeholder="Enter Train Number"
                      value={formik.values.train_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.train_number &&
                        formik.touched.train_number
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.train_number}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Booking Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="booking_number"
                      placeholder="Enter Booking Number"
                      value={formik.values.booking_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.booking_number &&
                        formik.touched.booking_number
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.booking_number}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Origin
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="origin"
                      placeholder="Enter Origin"
                      value={formik.values.origin}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.origin && formik.touched.origin
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.origin}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              )}

              {/* Air Fields */}
              {formik.values.transporter_type === "Air" && (
                <Row className="mb-3">
                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Carrier Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="carrier_name"
                      placeholder="Enter Carrier Name"
                      value={formik.values.carrier_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.carrier_name &&
                        formik.touched.carrier_name
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.carrier_name}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Flight Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="flight_number"
                      placeholder="Enter Flight Number"
                      value={formik.values.flight_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.flight_number &&
                        formik.touched.flight_number
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.flight_number}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      AWB Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="awb_number"
                      placeholder="Enter AWB Number"
                      value={formik.values.awb_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.awb_number && formik.touched.awb_number
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.awb_number}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Origin
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="origin"
                      placeholder="Enter Origin"
                      value={formik.values.origin}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.origin && formik.touched.origin
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.origin}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              )}

              {/* Remarks field - shown for all transport types */}
              {formik.values.transporter_type && (
                <Row className="mb-3">
                  <Col xs={12} md={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Remark
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="transport_remarks"
                      placeholder="Enter Transport Remark"
                      value={formik.values.transport_remarks}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={commonInputStyle}
                    />
                  </Col>
                </Row>
              )}
            </div>
          </Col>
        </Row>

        <Row className="mb-3 mt-3">
          <Col lg={6} className="mb-3">
            <AddTags
              value={formik.values.tags || []}
              onChange={(tags) => formik.setFieldValue("tags", tags)}
            />
          </Col>
        </Row>
        <hr />

        <div className="my-4">
          {!formik?.values?.sales_order ? (
            <div className="alert alert-warning text-center">
              Please select a Sales Order Number
            </div>
          ) : (
            <>
              {/* ================= Customer Details ================= */}
              <h5 className="fw-bold mb-3">Customer Details</h5>

              <div
                className="p-4 rounded-3 mb-4"
                style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
              >
                <div className="row mb-3">
                  <div className="col-md-6">
                    <p>
                      <strong>Company Name :</strong>{" "}
                      {selesOrderData?.customer_details?.company_name || "-"}
                    </p>
                    <p>
                      <strong>Company Email :</strong>{" "}
                      {selesOrderData?.customer_details?.email || "-"}
                    </p>
                    <p>
                      <strong>Customer PO Number :</strong>{" "}
                      {selesOrderData?.customer_details?.customer_po_number ||
                        "-"}
                    </p>
                    <p>
                      <strong>PO Document :</strong>{" "}
                      {selesOrderData?.customer_details
                        ?.customer_po_documents &&
                      Array.isArray(
                        selesOrderData?.customer_details?.customer_po_documents,
                      ) &&
                      selesOrderData?.customer_details?.customer_po_documents
                        .length > 0 ? (
                        <div>
                          {selesOrderData?.customer_details?.customer_po_documents.map(
                            (doc: string, index: number) => (
                              <div key={index} className="mb-1">
                                <a
                                  href={doc}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-decoration-underline"
                                >
                                  View Document {index + 1}
                                </a>
                              </div>
                            ),
                          )}
                        </div>
                      ) : selesOrderData?.customer_details
                          ?.customer_po_document_url ? (
                        <a
                          href={
                            selesOrderData?.customer_details
                              ?.customer_po_document_url
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-underline"
                        >
                          View Document
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </p>
                  </div>

                  <div className="col-md-6">
                    <p>
                      <strong>Industry :</strong>{" "}
                      {selesOrderData?.customer_details?.industry || "-"}
                    </p>
                    <p>
                      <strong>Company Phone No. :</strong>{" "}
                      {selesOrderData?.customer_details?.phone_number || "-"}
                    </p>
                    <p>
                      <strong>PO Date :</strong>{" "}
                      {selesOrderData?.customer_details?.customer_po_date ? (
                        <FormattedDate
                          date={
                            selesOrderData.customer_details.customer_po_date
                          }
                        />
                      ) : (
                        "-"
                      )}
                    </p>
                  </div>
                </div>

                <hr />

                {/* Additional Contact Details */}
                <h6 className="fw-bold mb-3">Additional Contact Details</h6>

                {selesOrderData?.customer_details?.contact_details?.length >
                0 ? (
                  <div className="row">
                    {selesOrderData.customer_details.contact_details.map(
                      (contact: any, index: number) => (
                        <div key={index} className="col-md-6 mb-3">
                          <p>
                            <strong>Contact Name :</strong>{" "}
                            {contact.name || "-"}
                          </p>
                          <p>
                            <strong>Email :</strong> {contact.email || "-"}
                          </p>
                          <p>
                            <strong>Phone Number :</strong>{" "}
                            {contact.phone_number || "-"}
                          </p>
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <p className="text-muted mb-0">
                    No additional contacts available.
                  </p>
                )}
              </div>

              {/* ================= Billing Details ================= */}
              <h5 className="fw-bold mb-3">Billing Details</h5>

              <div
                className="p-4 rounded-3"
                style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
              >
                <div className="row">
                  <div className="col-md-6">
                    <p className="fw-bold mb-1">Bill From :</p>
                    {selesOrderData?.bill_from ? (
                      <p className="mb-0">
                        {selesOrderData.bill_from.org_address}
                        <br />
                        {`${selesOrderData.bill_from.org_city}, ${selesOrderData.bill_from.org_state}, ${selesOrderData.bill_from.org_country} - ${selesOrderData.bill_from.org_pincode}`}
                      </p>
                    ) : (
                      <p className="text-muted mb-0">Not available</p>
                    )}
                  </div>

                  <div className="col-md-6">
                    <p className="fw-bold mb-1">Bill To :</p>
                    {selesOrderData?.bill_to ? (
                      <p className="mb-0">
                        {selesOrderData.bill_to.address}
                        <br />
                        {`${selesOrderData.bill_to.city}, ${selesOrderData.bill_to.state}, ${selesOrderData.bill_to.country} - ${selesOrderData.bill_to.pincode}`}
                      </p>
                    ) : (
                      <p className="text-muted mb-0">Not available</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div>
          <h5 className="fw-bold mb-3">Product Details</h5>
          <div
            className="table-responsive p-3 mb-4"
            style={{
              backgroundColor: MecordColors.ActiveBackground,
              borderRadius: "10px",
            }}
          >
            <div className="overflow-auto" style={{}}>
              <table
                style={{
                  width: "100%",
                  minWidth: "2000px",
                  borderCollapse: "collapse",
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                }}
              >
                <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
                  <tr>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Sr No.
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Packing No.
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Products
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Make
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Model
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Product Code
                    </th>
                    {/* <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Serviceable
                    </th> */}
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Ordered Quantity
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Quantity
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Unit
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Unit Price
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Rate in Currency
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Gross Value
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Discount/Flat
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Discount Value
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Net Value
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      GST(%)
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      SGST(%)
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      CGST(%)
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      IGST(%)
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      GST Value
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "left",
                      }}
                    >
                      Total
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "center",
                      }}
                    >
                      Additional Details
                    </th>
                    <th
                      style={{
                        minWidth: "100px",
                        position: "sticky",
                        right: 0,
                        background: MecordColors.LightGrayColor,
                        zIndex: 2,
                        textAlign: "left",
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: "#fff" }}>
                  {packingProducts?.length > 0 ? (
                    packingProducts.map((prod: any, idx: number) => (
                      <tr
                        key={`${prod.packing_id || prod.packing_done_id || idx}_${prod.product_id || idx}_${idx}`}
                      >
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {idx + 1}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {prod?.packing_number || "-"}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                            }}
                          >
                            {prod?.product_name || "-"}
                          </div>
                          <div
                            style={{
                              color: "#666",
                              fontSize: "0.65rem",
                            }}
                          >
                            SKU: {prod?.product_sku || prod?.sku || "N/A"} | HSN
                            Code: {prod?.hsn_code || "N/A"}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {prod?.make_name || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {prod?.model_name || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {prod?.product_code || "N/A"}
                        </td>
                        {/* <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                            textAlign: "center",
                          }}
                        >
                          {prod?.serviceable || prod?.is_serviceable
                            ? "Yes"
                            : "No"}
                        </td> */}
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {prod?.order_quantity || 0}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {prod?.quantity || 0}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {prod?.unit_name || "-"}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {currencySymbol}{" "}
                          {formatIndianAmount(prod?.unit_price)}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {currencySymbol}{" "}
                          {formatIndianAmount(prod?.rate_in_currency)}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {currencySymbol}{" "}
                          {formatIndianAmount(
                            prod?.gross_value || prod?.item_amount,
                          )}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {(() => {
                            const discountValueInput =
                              parseFloat(prod?.discount_value) || 0;
                            const discountType =
                              prod?.discount_type || "currency";

                            if (
                              discountType === "%" ||
                              discountType === "percentage"
                            ) {
                              return `${formatIndianAmount(discountValueInput)}%`;
                            } else {
                              return "Flat";
                            }
                          })()}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {(() => {
                            const discountValueInput =
                              parseFloat(prod?.discount_value) ||
                              parseFloat(prod?.discount_amount);
                            const discountType =
                              prod?.discount_type || "currency";
                            const grossValue =
                              parseFloat(
                                prod?.gross_value || prod?.item_amount || 0,
                              ) || 0;

                            let calculatedDiscountAmount = 0;
                            if (
                              discountType === "%" ||
                              discountType === "percentage"
                            ) {
                              calculatedDiscountAmount =
                                (grossValue * discountValueInput) / 100;
                            } else {
                              calculatedDiscountAmount = Math.min(
                                discountValueInput,
                                grossValue,
                              );
                            }

                            return `${currencySymbol}${formatIndianAmount(calculatedDiscountAmount)}`;
                          })()}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {currencySymbol}{" "}
                          {formatIndianAmount(
                            prod?.net_taxable_amount ||
                              prod?.net_taxable_value ||
                              prod?.net_value ||
                              0,
                          )}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {prod?.gst_percent || prod?.gst_percentage || "-"}
                          {prod?.gst_percent || prod?.gst_percentage ? "%" : ""}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {prod?.sgst_percent || prod?.sgst_percentage || "-"}
                          {prod?.sgst_percent || prod?.sgst_percentage
                            ? "%"
                            : ""}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {prod?.cgst_percent || prod?.cgst_percentage || "-"}
                          {prod?.cgst_percent || prod?.cgst_percentage
                            ? "%"
                            : ""}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {prod?.igst_percent || prod?.igst_percentage || "-"}
                          {prod?.igst_percent || prod?.igst_percentage
                            ? "%"
                            : ""}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {currencySymbol}{" "}
                          {formatIndianAmount(
                            prod?.gst_amount || prod?.gst_value || 0,
                          )}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {currencySymbol}{" "}
                          {formatIndianAmount(
                            prod?.total || prod?.total_amount || 0,
                          )}
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {!!prod?.additional_details && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setAdditionalDetailsDraft(
                                  prod.additional_details,
                                );
                                setShowAdditionalDetailsModal(true);
                              }}
                            >
                              <InfoOutlined
                                style={{
                                  color: MecordColors.ThemeBGColor,
                                  fontSize: "20px",
                                }}
                              />
                            </IconButton>
                          )}
                        </td>
                        <td
                          style={{
                            minWidth: "100px",
                            position: "sticky",
                            right: 0,
                            backgroundColor: "white",
                            zIndex: 2,
                            textAlign: "left",
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              backgroundColor: MecordColors?.ThemeBGColor,
                              color: "#FFF",
                            }}
                            onClick={() => {
                              setModalProducts(prod?.products || []);
                              setModalPackingNumber(
                                prod?.packing_number || prod?.packing_no || "",
                              );
                              setModalPackingIndex(idx);
                              setShowProductsModal(true);
                            }}
                            title="View Products"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={22}
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "center",
                          backgroundColor: "white",
                          color: "#666",
                          paddingTop: "20px",
                          paddingBottom: "20px",
                        }}
                      >
                        No products added yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Products Modal */}
          <Modal
            show={showProductsModal}
            onHide={() => setShowProductsModal(false)}
            size="xl"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                Products for Packing: {modalPackingNumber || "-"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ overflowX: "auto", width: "100%" }}>
              <Table className="table align-middle">
                <thead>
                  <tr style={{ backgroundColor: MecordColors.LightGrayColor }}>
                    <th style={{ minWidth: "80px" }}>Sr. No.</th>
                    <th style={{ minWidth: "150px" }}>Product Name</th>
                    <th style={{ minWidth: "150px" }}>Product Code</th>
                    <th style={{ minWidth: "150px" }}>Make</th>
                    <th style={{ minWidth: "150px" }}>Model</th>
                    <th style={{ minWidth: "150px" }}>Serial No.</th>
                  </tr>
                </thead>
                <tbody>
                  {modalProducts && modalProducts.length ? (
                    modalProducts.map((p: any, i: number) => {
                      const serial =
                        p.serial_number ??
                        p.serial_no ??
                        (p.serials
                          ? Array.isArray(p.serials)
                            ? p.serials.join(", ")
                            : p.serials
                          : null) ??
                        String(i + 1);
                      return (
                        <tr key={p.product_id || i}>
                          <td>{i + 1}</td>
                          <td>{p.product_name || p.name || "-"}</td>
                          <td>{p.product_code || p.code || "-"}</td>
                          <td>{p.make_name || p.make || "-"}</td>
                          <td>{p.model_name || p.model || "-"}</td>
                          <td>{serial}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        No products available
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Modal.Body>
            <Modal.Footer>
              <div className="d-flex justify-content-center w-100">
                <CancelButton
                  showConfirmation={false}
                  onClick={() => setShowProductsModal(false)}
                  disabled={isSubmitting}
                />
              </div>
            </Modal.Footer>
          </Modal>

        {/* Additional Details Modal (View only) */}
        <Modal
          show={showAdditionalDetailsModal}
          onHide={() => {
            setShowAdditionalDetailsModal(false);
            setAdditionalDetailsDraft("");
          }}
          centered
          className="modal-lg"
        >
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "1rem", fontWeight: "600" }}>
              Additional Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div
              className="p-3 border rounded bg-light"
              style={{ minHeight: "100px", maxHeight: "400px", overflowY: "auto" }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(additionalDetailsDraft || "No additional details provided."),
              }}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowAdditionalDetailsModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
        </div>
        <Row>
          <Col md={6}>
            {selesOrderData &&
              selesOrderData?.terms_and_conditions &&
              selesOrderData?.terms_and_conditions.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 fw-bold">Terms & Conditions</h5>
                  <div
                    className="border rounded-3 overflow-hidden bg-white"
                    style={{ borderColor: "#e9ebec" }}
                  >
                    {groupedTerms.map(
                      (
                        [type, options]: [string, string[]],
                        index: number,
                        arr: any,
                      ) => (
                        <React.Fragment key={index}>
                          <div className="p-2">
                            <h6 className="fw-bold fs-14 mb-2">{type}</h6>
                            <ul
                              className="text-muted fs-13 ps-3 mb-0"
                              style={{ listStyleType: "disc" }}
                            >
                              {options.map((option, idx) => (
                                <li key={idx} className="mb-1">
                                  {option}
                                </li>
                              ))}
                            </ul>
                          </div>
                          {index !== arr.length - 1 && (
                            <hr
                              className="my-0 mx-2"
                              style={{
                                borderTop: "1px solid #e9ebec",
                                opacity: 1,
                              }}
                            />
                          )}
                        </React.Fragment>
                      ),
                    )}
                  </div>
                </div>
              )}
          </Col>
          <Col md={6}>
            <div
              className="p-4 rounded-3"
              style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
            >
              <h5 className="fw-bold mb-3">Total Summary</h5>
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-semibold">Subtotal:</span>
                <span>
                  {currencySymbol}
                  {formatIndianAmount(summaryTotals.subtotal)}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-semibold">Total Product Discount:</span>
                <span>
                  {currencySymbol}
                  {formatIndianAmount(summaryTotals.total_product_discount)}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-semibold">Net Taxable Amount:</span>
                <span>
                  {currencySymbol}
                  {formatIndianAmount(summaryTotals.net_taxable_amount)}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-semibold">Total GST Amount:</span>
                <span>
                  {currencySymbol}
                  {formatIndianAmount(summaryTotals.total_gst_amount)}
                </span>
              </div>
              <hr />
              <div className="d-flex justify-content-between fw-bold fs-16">
                <span>Total:</span>
                <span style={{ color: MecordColors.ActiveText }}>
                  {currencySymbol}
                  {formatIndianAmount(summaryTotals.total)}
                </span>
              </div>
            </div>
          </Col>
        </Row>

        <style>
          {`
              .accordion-terms-conditions .accordion-button,
              .accordion-note .accordion-button,
              .accordion-attach-files .accordion-button {
                background-color: ${MecordColors?.LightGrayColor} !important;
                color: ${MecordColors?.NormalText} !important;
              }
              .accordion-terms-conditions .accordion-button:not(.collapsed),
              .accordion-note .accordion-button:not(.collapsed),
              .accordion-attach-files .accordion-button:not(.collapsed) {
                background-color: ${MecordColors?.LightGrayColor} !important;
                color: ${MecordColors?.NormalText} !important;
              }
            `}
        </style>

        <Row className="mb-4 mt-3">
          <Col md={6}>
            <Accordion defaultActiveKey="0" className="mb-3 accordion-note">
              <Accordion.Item eventKey="0">
                <Accordion.Header>Notes</Accordion.Header>
                <Accordion.Body>
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="note"
                      placeholder="Enter note"
                      value={formik.values.note || ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Col>
          <Col md={6}>
            <Accordion
              defaultActiveKey="0"
              className="mb-3 accordion-attach-files"
            >
              <Accordion.Item eventKey="0">
                <Accordion.Header>Attach Files</Accordion.Header>
                <Accordion.Body>
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      name="attached_files"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const files = e.target.files;
                        if (files && files.length > 5) {
                          toast.error(
                            "Maximum 5 files allowed. Please select 5 or fewer files.",
                          );
                          e.target.value = ""; // Clear the input
                          formik.setFieldValue("attached_files", null);
                          formik.setFieldError(
                            "attached_files",
                            "Maximum 5 files allowed",
                          );
                        } else {
                          formik.setFieldValue("attached_files", files);
                          formik.setFieldError("attached_files", undefined);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      style={commonInputStyle}
                      isInvalid={
                        formik.touched.attached_files &&
                        !!formik.errors.attached_files
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.attached_files}
                    </Form.Control.Feedback>
                    {/* <Form.Text
                      className="text-muted"
                      style={{ fontSize: "0.8rem" }}
                    >
                      Maximum 5 files allowed
                    </Form.Text> */}
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Col>
        </Row>

        {/* Buttons */}
        <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
          <CancelButton onClick={handleCancelButton} disabled={isSubmitting} />
          <PrimarySubmitButton
            label="Submit"
            isLoading={isSubmitting}
            loadingLabel="Submitting..."
          />
        </div>
      </Form>
    </PageContainer>
  );
};

export default CreateDispatch;
